import { PartnerSales } from '../page';

interface PartnerListProps {
  partners: PartnerSales[];
  onViewPartner: (partner: PartnerSales) => void;
  onDeletePartner?: (partner: PartnerSales) => void;
  onApprovePartner?: (partner: PartnerSales, action: 'approve' | 'reject') => void;
  onViewRanking: () => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  statusFilter: 'all' | 'active' | 'inactive' | 'pending';
  onStatusFilterChange: (status: 'all' | 'active' | 'inactive' | 'pending') => void;
  sortBy: 'revenue' | 'orders' | 'commission';
  onSortByChange: (sort: 'revenue' | 'orders' | 'commission') => void;
  timeFilter: 'week' | 'month' | 'year';
  onTimeFilterChange: (filter: 'week' | 'month' | 'year') => void;
  currentUser: any;
}

export default function PartnerList({
  partners,
  onViewPartner,
  onDeletePartner,
  onApprovePartner,
  onViewRanking,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  onSortByChange,
  timeFilter,
  onTimeFilterChange,
  currentUser
}: PartnerListProps) {
  const isManager = currentUser?.role === 'manager';
  const pendingCount = partners.filter(p => p.status === 'pending').length;

  const getStatusBadge = (partner: PartnerSales) => {
    if (partner.status === 'pending') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <i className="ri-time-line mr-1"></i>
          Chờ phê duyệt
        </span>
      );
    } else if (partner.status === 'approved' && partner.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <i className="ri-check-line mr-1"></i>
          Đã phê duyệt
        </span>
      );
    } else if (partner.status === 'approved' && !partner.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <i className="ri-pause-line mr-1"></i>
          Tạm dừng
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <i className="ri-close-line mr-1"></i>
          Đã từ chối
        </span>
      );
    }
  };

  const getRevenueByTimeFilter = (partner: PartnerSales) => {
    switch (timeFilter) {
      case 'week':
        return partner.thisWeekRevenue;
      case 'month':
        return partner.thisMonthRevenue;
      case 'year':
        return partner.thisYearRevenue;
      default:
        return partner.totalRevenue;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Quản lý đối tác</h1>
          <p className="text-sm lg:text-base text-gray-600 mt-1">
            Theo dõi hiệu suất và quản lý đối tác bán hàng
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                {pendingCount} chờ phê duyệt
              </span>
            )}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          {isManager && (
            <button
              onClick={() => {/* Will be handled by parent component */}}
              className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-user-add-line mr-2"></i>
              Thêm đối tác
            </button>
          )}
          <button
            onClick={onViewRanking}
            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-trophy-line mr-2"></i>
            Xem bảng xếp hạng
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Tìm kiếm theo tên hoặc email..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="pending">Chờ phê duyệt</option>
              <option value="active">Đang hoạt động</option>
              <option value="inactive">Tạm dừng</option>
            </select>
          </div>

          {/* Sort */}
          <div>
            <select
              value={sortBy}
              onChange={(e) => onSortByChange(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
            >
              <option value="revenue">Sắp xếp theo doanh thu</option>
              <option value="orders">Sắp xếp theo đơn hàng</option>
              <option value="commission">Sắp xếp theo hoa hồng</option>
            </select>
          </div>
        </div>

        {/* Time Filter */}
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-sm text-gray-600 mr-2">Thời gian:</span>
          {[
            { key: 'week', label: 'Tuần này' },
            { key: 'month', label: 'Tháng này' },
            { key: 'year', label: 'Năm này' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => onTimeFilterChange(key as any)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                timeFilter === key
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 lg:p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Danh sách đối tác ({partners.length})
          </h3>
        </div>

        {partners.length === 0 ? (
          <div className="p-8 text-center">
            <i className="ri-user-line text-4xl text-gray-400 mb-4"></i>
            <p className="text-gray-600">Không tìm thấy đối tác nào</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Đối tác
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trạng thái
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Doanh thu
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Đơn hàng
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hoa hồng
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {partners.map((partner) => (
                    <tr key={partner.partnerId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {partner.partnerName}
                          </div>
                          <div className="text-sm text-gray-500">{partner.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(partner)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getRevenueByTimeFilter(partner).toLocaleString('vi-VN')}đ
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {partner.totalOrders}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {partner.commissionRate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => onViewPartner(partner)}
                          className="text-blue-600 hover:text-blue-900 whitespace-nowrap"
                        >
                          Xem chi tiết
                        </button>
                        
                        {isManager && partner.status === 'pending' && onApprovePartner && (
                          <>
                            <button
                              onClick={() => onApprovePartner(partner, 'approve')}
                              className="text-green-600 hover:text-green-900 whitespace-nowrap"
                            >
                              Phê duyệt
                            </button>
                            <button
                              onClick={() => onApprovePartner(partner, 'reject')}
                              className="text-red-600 hover:text-red-900 whitespace-nowrap"
                            >
                              Từ chối
                            </button>
                          </>
                        )}
                        
                        {isManager && onDeletePartner && (
                          <button
                            onClick={() => onDeletePartner(partner)}
                            className="text-red-600 hover:text-red-900 whitespace-nowrap"
                          >
                            Xóa
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {partners.map((partner) => (
                <div key={partner.partnerId} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900">
                        {partner.partnerName}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">{partner.email}</p>
                    </div>
                    {getStatusBadge(partner)}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Doanh thu</p>
                      <p className="text-sm font-medium text-gray-900">
                        {getRevenueByTimeFilter(partner).toLocaleString('vi-VN')}đ
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Đơn hàng</p>
                      <p className="text-sm font-medium text-gray-900">{partner.totalOrders}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onViewPartner(partner)}
                      className="flex-1 min-w-0 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors"
                    >
                      Xem chi tiết
                    </button>
                    
                    {isManager && partner.status === 'pending' && onApprovePartner && (
                      <>
                        <button
                          onClick={() => onApprovePartner(partner, 'approve')}
                          className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors whitespace-nowrap"
                        >
                          Phê duyệt
                        </button>
                        <button
                          onClick={() => onApprovePartner(partner, 'reject')}
                          className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors whitespace-nowrap"
                        >
                          Từ chối
                        </button>
                      </>
                    )}
                    
                    {isManager && onDeletePartner && (
                      <button
                        onClick={() => onDeletePartner(partner)}
                        className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors whitespace-nowrap"
                      >
                        Xóa
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
